import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import { RunnerBaseSchema, StartBackgroundSessionParams } from "../ea.runner.types";
import {
  RunnerContextData,
  RecorderSyncDataRequest,
  RecorderSyncDataResponse,
  RunnerAuthRequest,
  RunnerAuthResponse,
  RunnerInitialDataResponse,
  RunnerLoadedRequest,
  RunnerLogRequest,
  RunnerPlayerEventRequest,
  RunnerStartResponse,
  RunnerStepLogRequest,
  RunnerTokensRequest,
} from "../ea.runner.next.types";
import { RunnerMode } from "../ea.enums";

export const RUNNER_BASE_PATH = "runner";
const runner = createEndpointBase(RUNNER_BASE_PATH);
const runnerNext = createEndpointBase(RUNNER_BASE_PATH, "next");

export const runnerEndpoints = {
  runnerNextStart: runnerNext({
    method: "POST",
    path: "start",
    request: z.any(),
    response: z.any(),
    requestType: {} as Partial<StartBackgroundSessionParams>,
    responseType: {} as RunnerStartResponse,
    description: "Starts a new runner execution for a script.",
  }),
  runnerNextData: runnerNext({
    method: "GET",
    path: "data",
    request: z.any(),
    response: z.any(),
    requestType: {} as { sessionId: string },
    responseType: {} as RunnerInitialDataResponse,
    description: "Retrieves context data for a runner session.",
  }),
  runnerNextReload: runnerNext({
    method: "GET",
    path: "reload",
    request: z.any(),
    response: z.any(),
    requestType: {} as { sessionId: string },
    responseType: {} as RunnerContextData,
    description: "Triggers a reload of the runner session context.",
  }),
  runnerNextSync: runnerNext({
    method: "POST",
    path: "sync",
    request: z.any(),
    response: z.any(),
    requestType: {} as RecorderSyncDataRequest,
    responseType: {} as RecorderSyncDataResponse,
    description: "Synchronizes the runner state with the server.",
  }),
  runnerNextTokens: runnerNext({
    method: "POST",
    path: "tokens",
    request: z.any(),
    response: z.any(),
    requestType: {} as RunnerTokensRequest,
    description: "Updates or sets authentication tokens for a runner session.",
  }),
  runnerNextOtp: runnerNext({
    method: "POST",
    path: "otp",
    request: z.object({ virtualUserId: z.number() }),
    response: z.any(),
    description: "Generates an OTP token for a virtual user.",
  }),
  runnerNextClose: runnerNext({
    method: "POST",
    path: "close",
    request: z.object({ sessionIds: z.array(z.string()), endDate: z.string().optional() }),
    response: z.any(),
    description: "Closes specified runner sessions and terminates their executions.",
  }),
  runnerNextSessionTabHeartbeat: runnerNext({
    method: "POST",
    path: "session-tab-heartbeat",
    request: z.object({ sessionId: z.string() }),
    response: z.any(),
    description: "Sends a heartbeat signal for a session tab to keep it alive.",
  }),
  runnerNextLoaded: runnerNext({
    method: "POST",
    path: "loaded",
    request: z.any(),
    response: z.any(),
    requestType: {} as RunnerLoadedRequest,
    description: "Marks the runner session as fully loaded and ready.",
  }),
  runnerNextScreenshot: runnerNext({
    method: "POST",
    path: "screenshot/:sessionId",
    request: z.any(),
    response: z.any(),
    requestType: {} as RunnerLoadedRequest,
    description: "Uploads a screenshot for a runner session.",
  }),
  runnerNextBackgroundVideo: runnerNext({
    method: "POST",
    path: "background-video",
    request: z.any(),
    response: z.any(),
    description: "Uploads a background video for script documentation.",
  }),
  runnerNextPlayerEvent: runnerNext({
    method: "POST",
    path: "player-event",
    request: z.any(),
    response: z.any(),
    requestType: {} as RunnerPlayerEventRequest,
    description: "Processes a user interaction event from the player.",
  }),
  runnerNextPlayerLog: runnerNext({
    method: "POST",
    path: "player-log",
    request: z.any(),
    response: z.any(),
    requestType: {} as RunnerLogRequest,
    description: "Updates the main execution log for a runner session.",
  }),
  runnerNextPlayerStepLog: runnerNext({
    method: "POST",
    path: "player-step-log",
    request: z.any(),
    response: z.any(),
    requestType: {} as RunnerStepLogRequest,
    description: "Updates a specific step log for a runner session.",
  }),
  runnerNextSequence: runnerNext({
    method: "POST",
    path: "sequence",
    request: z.object({ id: z.number() }),
    response: z.object({ value: z.string() }),
    description: "Retrieves the next value from a number sequence for the runner.",
  }),
  runnerNextAuth: runnerNext({
    method: "POST",
    path: "auth",
    request: RunnerBaseSchema.extend({ vuId: z.number(), onlyGet: z.boolean().optional() }),
    response: z.any(),
    requestType: {} as RunnerAuthRequest,
    responseType: {} as RunnerAuthResponse,
    description: "Authenticates a virtual user within a runner session.",
  }),
  runnerNextLogout: runnerNext({
    method: "POST",
    path: "logout",
    request: z.any(),
    response: z.any(),
    description: "Logs out a virtual user from a runner session.",
  }),

  runnerStart: runner({
    method: "POST",
    path: "start",
    request: z.any(),
    response: z.any(),
    description: "Starts a new runner execution for a script.",
  }),
  runnerNextSyncLegacy: runner({
    method: "POST",
    path: "nextSync",
    request: z.any(),
    response: z.any(),
    requestType: {} as RecorderSyncDataRequest,
    responseType: {} as RecorderSyncDataResponse,
  }),
  runnerHeartbeat: runner({
    method: "POST",
    path: "heartbeat",
    request: RunnerBaseSchema,
    response: z.any(),
    description: "Sends a heartbeat signal for a runner execution.",
  }),
  runnerMeta: runner({
    method: "GET",
    path: "meta",
    request: RunnerBaseSchema,
    response: z.object({ scriptId: z.number(), url: z.string(), mode: z.nativeEnum(RunnerMode) }),
    description: "Retrieves metadata for a specific runner session.",
  }),
  runnerInit: runner({
    method: "POST",
    path: "init",
    request: RunnerBaseSchema,
    response: z.any(),
  }),
} as const;
